/**
 * stripMarkdown — turn a Story field (which can contain full markdown
 * headers, lists, links, bold, etc.) into a clean single-paragraph plain
 * string suitable for a card excerpt.
 *
 * We are deliberately NOT a full markdown parser — this runs on every feed
 * card render, so it stays regex-only and does the minimum needed to fix
 * "## Ingredients - 1 Pizza dough…" → "Ingredients 1 Pizza dough…".
 *
 * What it strips / rewrites:
 *   - ATX headers   (leading #, ##, ###, up to ######)
 *   - Setext headers (a line of === or --- under a title — the underline goes)
 *   - Blockquotes   (leading >)
 *   - Bullet lists  (leading -, *, +) and numbered lists (1., 2., …)
 *   - Bold + italic (**a**, __a__, *a*, _a_)  → keeps `a`
 *   - Strikethrough (~~a~~) → `a`
 *   - Inline code   (`a`)   → `a`
 *   - Fenced code   (```...```) → dropped entirely
 *   - Images        (![alt](url)) → dropped (they aren't excerpt material)
 *   - Links         ([text](url)) → keeps `text`
 *   - Horizontal rules (---, ***, ___ on their own line)
 *   - Trailing double-space "hard break" markers
 *
 * Then collapses newlines + runs of whitespace to a single space so the
 * excerpt reads as a single line under a `line-clamp-2`.
 */
export function stripMarkdown(input) {
  if (!input) return "";
  let s = String(input);

  // Fenced code blocks first — everything between ``` fences goes.
  s = s.replace(/```[\s\S]*?```/g, " ");
  // Same for ~~~ fenced blocks (rare but valid).
  s = s.replace(/~~~[\s\S]*?~~~/g, " ");

  // Images ![alt](url) — drop entirely (an alt attribute in a card excerpt
  // would just read like a stray word). Do this before generic links or the
  // link rule would keep the alt text with a stray `!`.
  s = s.replace(/!\[[^\]]*]\([^)]*\)/g, " ");

  // Links [text](url) → text
  s = s.replace(/\[([^\]]+)]\(([^)]+)\)/g, "$1");

  // Reference-style link definitions on their own line: [id]: url
  s = s.replace(/^\s*\[[^\]]+]:\s+\S+.*$/gm, "");

  // Setext underlines under a preceding title: line of === or ---
  s = s.replace(/^\s*(=|-){2,}\s*$/gm, "");

  // Horizontal rules on their own line: --- *** ___
  s = s.replace(/^\s*(-\s*){3,}\s*$/gm, "");
  s = s.replace(/^\s*(\*\s*){3,}\s*$/gm, "");
  s = s.replace(/^\s*(_\s*){3,}\s*$/gm, "");

  // ATX headers: strip leading #s (and any closing hashes)
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  s = s.replace(/\s+#+\s*$/gm, "");

  // Blockquote markers at the start of a line
  s = s.replace(/^\s*>+\s?/gm, "");

  // Bullet markers at the start of a line: - / * / +
  s = s.replace(/^\s*[-*+]\s+/gm, "");
  // Numbered markers at the start of a line: 1. 2. 12)
  s = s.replace(/^\s*\d+[.)]\s+/gm, "");

  // Bold + italic (order matters: bold ** __ first, then italic single markers)
  s = s.replace(/\*\*(.+?)\*\*/g, "$1");
  s = s.replace(/__(.+?)__/g, "$1");
  s = s.replace(/(^|[^\w*])\*(?!\s)(.+?)\*(?!\w)/g, "$1$2");
  s = s.replace(/(^|[^\w_])_(?!\s)(.+?)_(?!\w)/g, "$1$2");

  // Strikethrough ~~text~~
  s = s.replace(/~~(.+?)~~/g, "$1");

  // Inline code `text`
  s = s.replace(/`+([^`]+?)`+/g, "$1");

  // Trailing hard-break spaces (markdown line break = two trailing spaces)
  s = s.replace(/  +$/gm, "");

  // Collapse whitespace + newlines to single spaces
  s = s.replace(/\s+/g, " ").trim();

  return s;
}

// Line patterns that signal "this is a recipe/list scaffolding line, not
// a prose sentence" — used by cardExcerpt to skip past ingredient dumps.
const SKIP_HEADING_RE = /^\s*(?:ingredients?|directions?|instructions?|steps?|method|prep|notes?|tips?|serves?|makes?|yields?|prep time|cook time|total time)\s*:?\s*$/i;
const LIST_ITEM_RE = /^\s*(?:[-*+]\s+|\d+[.)]\s+|#{1,6}\s+|>\s+)/;
// A "prose-looking" line has multiple spaces (i.e. multiple words) and
// isn't overwhelmingly numeric. This filters out `1 c flour` type rows
// even AFTER the list marker has been stripped.
const PROSE_LINE_RE = /\S+\s+\S+.*\S+/;
const MOSTLY_NUMERIC_RE = /^[\d\s\/.,]+(?:cup|c|tbsp|tsp|oz|lb|lbs|g|kg|ml|l|pinch|dash|slices?|cloves?|pieces?)\b/i;
// Any line that starts with a digit + a short (< 6 words) body is almost
// certainly an ingredient row ("2 eggs", "1 onion", "3 cloves garlic")
// rather than a real sentence — English prose rarely opens with a digit.
const DIGIT_START_SHORT_RE = /^\d/;

/**
 * Given the raw markdown `context` field, return the first paragraph-shaped
 * prose sentence — skipping headings ("Ingredients"), list rows ("- 1 c
 * flour"), and lines that are basically a measurement + ingredient. Used
 * as a fallback when the author didn't write an explicit `summary`.
 *
 * Returns `""` when the entire context is scaffolding (in which case the
 * card renders no excerpt — cleaner than a truncated ingredient dump).
 */
export function firstProseParagraph(input) {
  if (!input) return "";
  const raw = String(input);
  // Walk the raw markdown line-by-line. We need the ORIGINAL formatting
  // to identify list markers — stripMarkdown() erases them.
  const lines = raw.split(/\r?\n/);
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (LIST_ITEM_RE.test(trimmed)) continue;
    if (SKIP_HEADING_RE.test(trimmed)) continue;
    // Post-strip check: after markdown formatting is removed, does the line
    // still look like a real sentence?
    const clean = stripMarkdown(trimmed);
    if (!clean) continue;
    if (MOSTLY_NUMERIC_RE.test(clean)) continue;
    // Short line that starts with a digit — treat as an ingredient row
    // regardless of unit vocabulary ("2 eggs", "1 onion, diced").
    if (DIGIT_START_SHORT_RE.test(clean) && clean.split(/\s+/).length < 6) continue;
    if (!PROSE_LINE_RE.test(clean)) continue;
    return clean;
  }
  return "";
}

/**
 * Card excerpt picker. Prefers an author-written summary; falls back to the
 * first prose paragraph of the Story; and if the Story is entirely
 * ingredient scaffolding, returns "" so the card just shows the title.
 */
export function cardExcerpt(idea) {
  if (!idea) return "";
  if (idea.summary && String(idea.summary).trim()) {
    return String(idea.summary).trim();
  }
  const prose = firstProseParagraph(idea.context);
  return prose;
}

export default stripMarkdown;
